






























































































































































































import 'swiper/swiper.min.css';
import 'swiper/components/pagination/pagination.min.css';
import 'swiper/components/navigation/navigation.min.css';

import type { PropType } from '@nuxtjs/composition-api';
import {
  computed,
  defineComponent,
  inject,
  nextTick,
  onBeforeUnmount,
  onMounted,
  ref,
  watch
} from '@nuxtjs/composition-api';
import type { ProductImage } from '@vue-storefront/magento-api';
import { watchArray } from '@vueuse/core';
import VaimoFocusTrapWrapper from 'atoms/helper/VaimoFocusTrapWrapper.vue';
import VaimoBanner from 'atoms/VaimoBanner.vue';
import debounce from 'lodash.debounce';
import type { Swiper as SwiperInstance, SwiperOptions } from 'swiper';
import { directive } from 'vue-awesome-swiper';

import { useUiState } from '~/composables/useUiState';
import {
  useScreenSize,
  useVisibleBottomOffset
} from '~/diptyqueTheme/composable';
import { getHiddenElementHeight } from '~/diptyqueTheme/helpers/getHiddenElementHeight';

import SwiperNavigation from './SwiperCarousel/SwiperNavigation/index.vue';
import SwiperPagination from './SwiperCarousel/SwiperPagination/index.vue';

enum EditoImageTagEnum {
  DEFAULT = 'default',
  EDITO = 'edito',
  NIGHT = 'night'
}

type EditoImageTag =
  | EditoImageTagEnum.DEFAULT
  | EditoImageTagEnum.EDITO
  | EditoImageTagEnum.NIGHT;

interface EditoImage {
  id: string;
  tag: EditoImageTag;
  position: number;
  image: ProductImage;
}

export default defineComponent({
  name: 'ProductMainGallery',
  directives: {
    swiper: directive
  },
  components: {
    VaimoBanner,
    VaimoBadge: () => import('atoms/VaimoBadge.vue'),
    EngravingPleat: () => import('molecules/EngravingPleat/EngravingPleat.vue'),
    VaimoFocusTrapWrapper,
    SwiperNavigation,
    SwiperPagination
  },
  props: {
    additionalData: {
      required: true,
      type: Object
    },
    images: {
      required: true,
      type: Array as PropType<ProductImage[]>,
      default: () => []
    },
    showProductInfo: {
      type: Boolean,
      default: true
    },
    hasEngraving: {
      type: Boolean,
      default: false
    }
  },
  setup(props) {
    const { isMobile, isDesktop } = useScreenSize();
    const isHeaderTransparent = inject(
      'is-header-transparent',
      computed(() => false)
    );
    const swiperOptions: SwiperOptions = {
      autoHeight: true,
      normalizeSlideIndex: false,
      centerInsufficientSlides: true,
      spaceBetween: 0,
      slidesPerView: 1,
      breakpoints: {
        1024: {
          slidesPerView: 'auto',
          autoHeight: false
        }
      },
      loop: false,
      keyboard: { enabled: true },
      // Parallax effect is disabled for desktop devices via CSS
      parallax: { enabled: true },
      // Default pagination is disabled in favor of custom pagination
      pagination: false,
      // Default navigation is disabled in favor of custom navigation
      navigation: false,
      on: {
        init(swiper) {
          onSwiperStateChange(swiper);
        },
        slideChange(swiper) {
          onSwiperStateChange(swiper);
        }
      }
    };

    const swiperCarousel = ref(null);
    const swiperInstance = computed(() => swiperCarousel.value?.swiper || null);
    const activeIndex = computed(() => swiperInstance.value?.activeIndex ?? 0);
    const isSwiperPrevDisabled = ref<boolean>(false);
    const isSwiperNextDisabled = ref<boolean>(false);

    const onSwiperStateChange = (swiper) => {
      updateImageTag(swiper.activeIndex);
      updateSwiperNavigation(swiper);
    };

    /**
     * Update the state of the navigation buttons based on the swiper instance
     * @param swiper - The Swiper instance
     */
    const updateSwiperNavigation = ({
      activeIndex,
      isBeginning,
      isEnd,
      slides,
      params: { loop }
    }: SwiperInstance) => {
      isSwiperPrevDisabled.value = activeIndex === 0 || (isBeginning && !loop);
      isSwiperNextDisabled.value =
        activeIndex === slides.length - 1 || (isEnd && !loop);
    };

    const onSwiperPrevClick = () => {
      if (!swiperInstance.value) {
        return;
      }

      if (
        swiperInstance.value.isBeginning &&
        !swiperInstance.value.params.loop
      ) {
        return;
      }

      swiperInstance.value.slidePrev();
    };

    const onSwiperNextClick = () => {
      if (!swiperInstance.value) {
        return;
      }

      if (swiperInstance.value.isEnd && !swiperInstance.value.params.loop) {
        return;
      }

      swiperInstance.value.slideNext();
    };

    const onSwiperPaginationClick = (index: number) => {
      if (swiperInstance.value) {
        swiperInstance.value.slideTo(index);
      }
    };

    const swiperData = {
      activeIndex,
      swiperInstance,
      swiperOptions,
      swiperCarousel,
      isSwiperPrevDisabled,
      isSwiperNextDisabled,
      updateSwiperNavigation,
      onSwiperPrevClick,
      onSwiperNextClick,
      onSwiperPaginationClick,
      onSwiperStateChange
    };

    const aspectRatio = {
      default: {
        mobile: '3:4',
        desktop: '3:4'
      },
      night: {
        mobile: '3:4',
        desktop: '3:4'
      },
      edito: {
        mobile: '9:16',
        desktop: '3:4'
      }
    };

    const productEmptyImage: ProductImage = {
      url: '',
      position: 1,
      disabled: false,
      label: 'revamp'
    };

    const mainImage = computed<ProductImage>(
      () => props.images[0] ?? productEmptyImage
    );

    const IMAGE_LABELS = {
      // Revamp images
      REVAMP: 'revamp',
      // Edito images
      EDITO: 'edito',
      NIGHT: 'night'
    };

    /**
     * Helper functions to determine REVAMP image type
     */
    const isRevampImage = (image: ProductImage): boolean => {
      return image.label === IMAGE_LABELS.REVAMP;
    };

    /**
     * Helper functions to determine EDITO image type
     */
    const isEditoImage = (image: ProductImage): boolean => {
      return image.label === IMAGE_LABELS.EDITO;
    };

    /**
     * Helper functions to determine NIGHT image type
     */
    const isNightImage = (image: ProductImage): boolean => {
      return image.label === IMAGE_LABELS.NIGHT;
    };

    /**
     * Function to get the image tag based on the image label
     */
    const getImageTag = (image: ProductImage): EditoImageTag => {
      const tagMap: Record<string, EditoImageTag> = {
        [IMAGE_LABELS.NIGHT]: EditoImageTagEnum.NIGHT,
        [IMAGE_LABELS.EDITO]: EditoImageTagEnum.EDITO,
        [IMAGE_LABELS.REVAMP]: EditoImageTagEnum.DEFAULT
      };
      return tagMap[image.label] ?? EditoImageTagEnum.DEFAULT;
    };

    const getFilteredImages = (
      images: ProductImage[],
      mobile = false
    ): EditoImage[] => {
      return images
        .filter((image) => {
          const validType =
            isRevampImage(image) || isEditoImage(image) || isNightImage(image);

          // Exclude Edito images on desktop
          if (!mobile && isEditoImage(image)) {
            return false;
          }

          // Exclude images that are disabled on mobile
          if (mobile && image.disabled_mobile) {
            return false;
          }

          return validType;
        })
        .map((image, index) => ({
          id: `image-${index}`,
          tag: getImageTag(image),
          position: image.position,
          image
        }))
        .sort((a, b) => a.position - b.position);
    };

    const filteredGallery = computed<EditoImage[]>(() => {
      const filteredImages = getFilteredImages(props.images, !isDesktop.value);

      // If no filtered images, return the main image
      if (!filteredImages.length) {
        return [
          {
            id: 'image-main',
            position: 0,
            image: mainImage.value,
            tag: EditoImageTagEnum.DEFAULT
          }
        ];
      }

      return filteredImages;
    });

    /*
     * Pagination spacing and product info height
     */
    const paginationMargin = ref(0);
    const productInfo = ref<HTMLDivElement | null>(null);
    const getProductInfoHeight = () => {
      paginationMargin.value = productInfo.value
        ? getHiddenElementHeight(productInfo.value)
        : 0;
    };

    const RESIZE_DEBOUNCE_DELAY = 300;
    const debouncedResize = debounce(() => {
      getProductInfoHeight();
    }, RESIZE_DEBOUNCE_DELAY);

    watchArray(filteredGallery, () => {
      // Update the image tag when the gallery changes
      updateImageTag(activeIndex.value);

      if (swiperInstance.value) {
        // Update swiper instance
        swiperInstance.value.update();
        // Scroll to the first slide as hidden slides can cause issues
        swiperInstance.value.slideTo(0, 0);
      }
    });

    onMounted(() => {
      if (props.showProductInfo) {
        getProductInfoHeight();
        window.addEventListener('resize', debouncedResize);
      }
    });

    onBeforeUnmount(() => {
      if (props.showProductInfo) {
        window.removeEventListener('resize', debouncedResize);
      }
    });

    /*
     * Image tagging and filtering
     */
    const imageTag = ref<EditoImageTag>(EditoImageTagEnum.DEFAULT);

    /*
     * Determine if the current image tag is NIGHT mode
     */
    const nightMode = computed<boolean>(
      () => imageTag.value === EditoImageTagEnum.NIGHT
    );

    /**
     * Update the image tag based on the current index
     * @param index - The index of the current image
     */
    const updateImageTag = (index: number) => {
      const image = filteredGallery.value[index];
      if (image) {
        imageTag.value = image.tag || EditoImageTagEnum.DEFAULT;
      }
    };

    const imageTagData = {
      imageTag,
      nightMode,
      updateImageTag
    };

    /*
     * Editorial info
     */
    const loading = ref(false);

    onMounted(() => {
      nextTick(() => {
        loading.value = true;
      });
    });

    /*
     * Zoom functionality for desktop
     */
    const carouselZoomed = ref(null);
    const zoomedOnDesktop = ref(false);
    const desktopZoom = (index: number) => {
      // Validate if carouselZoomed is defined and not null
      if (!carouselZoomed.value) {
        return;
      }

      // If on mobile, do not zoom
      if (!isDesktop.value) {
        return;
      }

      // Set the initial index for the zoomed carousel
      carouselZoomed.value.$el.style.width =
        Math.round((window.innerHeight / 4) * 3) + 'px';
      carouselZoomed.value.goTo(index, true);
      zoomedOnDesktop.value = true;
    };

    const galleryWrapperClasses = ref([]);
    const GALLERY_TIMEOUT = 50;
    watch(zoomedOnDesktop, () => {
      if (zoomedOnDesktop.value) {
        galleryWrapperClasses.value.push('zoomed');
        setTimeout(() => {
          galleryWrapperClasses.value.push('fadeIn');
        }, GALLERY_TIMEOUT);
      } else {
        galleryWrapperClasses.value = [];
      }
    });

    const zoomData = {
      galleryWrapperClasses,
      carouselZoomed,
      zoomedOnDesktop,
      desktopZoom
    };

    /*
     * Engraving Info
     */
    const { openEngravingInfo } = useUiState();
    const productGalleryRef = ref<HTMLElement | null>(null);
    const { bottomOffset } = useVisibleBottomOffset(productGalleryRef);

    const engravingData = {
      bottomOffset,
      productGalleryRef,
      openEngravingInfo
    };

    return {
      ...swiperData,
      isHeaderTransparent,
      productInfo,
      paginationMargin,
      aspectRatio,
      loading,
      mainImage,
      filteredGallery,
      isMobile,
      ...imageTagData,
      ...engravingData,
      ...zoomData
    };
  }
});
